/**
 * TempCheckpointInput — temperature entry for one checkpoint (Kitchen or
 * Drop-off) of one deliverable, sized for a table cell.
 *
 * A dropdown of quick presets plus a "Custom…" option that reveals a
 * numeric input for an exact reading. Which presets show depends on the
 * row's HACCP class (`temperatureClass`):
 *
 *   - hot / chilled / unclassified → Hot · 165.0°F and Chilled · 38.0°F.
 *     The stored value is a concrete °F number (or null while unset).
 *   - ambient (shelf-stable) → a single "Neutral" choice. Neutral means
 *     "room temperature, no reading required" and stores NO number (null):
 *     absence-of-reading is the platform's representation for ambient
 *     items (the cafeteria wizards' room-temp exemption), and any °F
 *     between the hold bands would stamp OUT_OF_RANGE on the signed
 *     compliance record. A genuine measurement still goes through Custom.
 *
 * There is deliberately NO room-temp preset on HOT/CHILLED rows: a
 * one-tap 70°F records an out-of-range holding violation on a signed
 * compliance record (the Chattahoochee 70°F rows).
 *
 * The custom-input slot is always reserved so the row width stays
 * constant whether or not "Custom" is selected. Composed from `@ordo/ui`
 * primitives (Select + Input) — a feature component, not a UI primitive,
 * per the UI-primitives policy.
 *
 * On a TOUCH device the custom box behaves like every count box in cafeteria
 * mode (`CountInput`): it asks iOS for no keyboard and shows the app's keypad
 * in its decimal layout while focused, with Done to close it (Abner,
 * 2026-09-15: every numeric input on a tablet uses the custom keyboard).
 * Picking "Custom…" hands the focus straight to the box, so the pad opens
 * without a second tap.
 */

import { cn } from '@ordo/ui/cn'
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from '@ordo/ui/input-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { useRef, useState } from 'react'

import { NumericKeypadPopover } from '@/components/cafeteria/count-input'
import { useCoarsePointer } from '@/hooks/use-coarse-pointer'
import { applyDecimalKeypadKey, sanitizeDecimalText } from '@/lib/cafeteria/numeric-keypad'

/** Row HACCP class the checkpoint belongs to; null/undefined = unclassified. */
export type TempCheckpointClass = 'hot' | 'chilled' | 'ambient' | null

/** One dropdown preset; `temperatureF: null` = a no-reading choice (Neutral). */
export interface TempPresetOption {
  key: string
  label: string
  temperatureF: number | null
}

/**
 * Quick-pick presets for HOT/CHILLED/unclassified rows. Hot clears the 135°F
 * hot-hold; Chilled clears the 41°F cold-hold. 165/38 match
 * TEMP_CLASS_PREFILL_F (aggregate-deliveries.ts) so a classed item's prefill
 * renders as its labeled preset, not "Custom…".
 */
export const TEMP_PRESETS: readonly TempPresetOption[] = [
  { key: 'hot', label: 'Hot', temperatureF: 165 },
  { key: 'chilled', label: 'Chilled', temperatureF: 38 },
] as const

/** The shelf-stable choice: room temperature, no reading recorded. */
export const NEUTRAL_PRESET: TempPresetOption = { key: 'neutral', label: 'Neutral', temperatureF: null }

/** Presets offered for a row of the given HACCP class. Pure / unit-tested. */
export function presetOptionsForClass(temperatureClass?: TempCheckpointClass): readonly TempPresetOption[] {
  return temperatureClass === 'ambient' ? [NEUTRAL_PRESET] : TEMP_PRESETS
}

/**
 * The Select's controlled value for the current state. Ambient rows treat
 * null as the (pre-filled) Neutral selection — never a blank placeholder —
 * and any concrete number as Custom. Other rows keep the original behavior:
 * a preset temp renders as its preset, other numbers as Custom, null as the
 * "Set temp" placeholder. Pure / unit-tested.
 */
export function deriveSelectValue(
  temperatureClass: TempCheckpointClass | undefined,
  value: number | null,
  customChosen: boolean,
): 'custom' | 'neutral' | 'hot' | 'chilled' | '' {
  if (customChosen) {
    return 'custom'
  }
  if (temperatureClass === 'ambient') {
    return value === null ? 'neutral' : 'custom'
  }
  const preset = TEMP_PRESETS.find((p) => p.temperatureF === value)
  if (preset) {
    return preset.key as 'hot' | 'chilled'
  }
  return value === null ? '' : 'custom'
}

interface TempCheckpointInputProps {
  value: number | null
  onChange: (next: number | null) => void
  ariaLabel: string
  /** Row HACCP class — ambient rows offer Neutral instead of Hot/Chilled. */
  temperatureClass?: TempCheckpointClass
  /** Read-only once the day is submitted/finalized. */
  disabled?: boolean
  /**
   * `compact`: the reading box alone, no preset dropdown and no unit, since
   * the column header carries the °F (the production record's input table,
   * PROD-1441). The value is typed or pre-filled; the keypad, the one-decimal
   * draft and the null-on-clear rule are the same.
   */
  variant?: 'full' | 'compact'
  /** Draws the box in the alert tone: a reading outside the item's holding band. Compact only. */
  alert?: boolean
}

/**
 * Keep only digits plus at most one decimal separator with at most one
 * decimal digit — readings persist as Decimal(5,1), so 48.0 / 41.5 are
 * the finest grain the log stores. The rule lives with the keypad
 * (`sanitizeDecimalText`) so a press and a keystroke agree.
 */
export function sanitizeTempText(raw: string): string {
  return sanitizeDecimalText(raw)
}

export function TempCheckpointInput({
  value,
  onChange,
  ariaLabel,
  temperatureClass = null,
  disabled = false,
  variant = 'full',
  alert = false,
}: TempCheckpointInputProps) {
  // Track an explicit "Custom" pick so the numeric input stays open even
  // if the operator types a value that happens to equal a preset. Custom
  // mode is ALSO derived from the value itself, so a non-preset reading
  // set externally (e.g. pre-filled from a photo) shows up immediately
  // without the operator having to switch to Custom by hand.
  const [customChosen, setCustomChosen] = useState(false)
  // While the operator is typing, echo their keystrokes verbatim so
  // intermediate states like "101." survive; at rest, render the stored
  // value at the log's one-decimal precision ("48" reads back as "48.0").
  const [draft, setDraft] = useState<string | null>(null)
  // Touch devices get the app's decimal keypad instead of the system keyboard.
  const coarsePointer = useCoarsePointer()
  const [customFocused, setCustomFocused] = useState(false)
  const customRef = useRef<HTMLInputElement>(null)
  // Set when the operator picks "Custom…": the Select would hand the focus
  // back to its trigger on close; it goes to the new box instead.
  const focusCustomOnClose = useRef(false)
  const options = presetOptionsForClass(temperatureClass)
  const selectValue = deriveSelectValue(temperatureClass, value, customChosen)
  const isCustom = selectValue === 'custom'
  const keypad = coarsePointer && !disabled
  const customText = draft ?? (value === null ? '' : value.toFixed(1))

  /** One edit of the custom text, from a keystroke or a pad press. */
  function commitText(text: string) {
    // Editing the box IS choosing Custom: a box that opened for a non-preset
    // reading must not vanish under the finger when the text is cleared
    // (null would otherwise read as "no preset picked" and unmount it).
    setCustomChosen(true)
    setDraft(text)
    const parsed = Number(text)
    onChange(text === '' || text === '.' || Number.isNaN(parsed) ? null : parsed)
  }

  function handleSelect(next: string) {
    if (next === 'custom') {
      setCustomChosen(true)
      focusCustomOnClose.current = true
      return
    }
    setCustomChosen(false)
    setDraft(null)
    const preset = options.find((p) => p.key === next)
    // Neutral carries no number — selecting it clears the reading.
    onChange(preset ? preset.temperatureF : null)
  }

  if (variant === 'compact') {
    return (
      <NumericKeypadPopover
        open={keypad && customFocused}
        decimal
        onKey={(key) => {
          if (key.kind === 'confirm') {
            customRef.current?.blur()
            return
          }
          commitText(applyDecimalKeypadKey(customText, key))
        }}>
        {/* 64px: `165.0` with the box's 8px padding, so the table's temperature columns stay compact. */}
        <InputGroup
          className={cn('h-8 w-16 shrink-0', alert && 'border-destructive/50 bg-destructive/5')}
          data-alert={alert ? 'true' : undefined}>
          <InputGroupInput
            ref={customRef}
            inputMode={keypad ? 'none' : 'decimal'}
            value={customText}
            disabled={disabled}
            onChange={(e) => commitText(sanitizeDecimalText(e.target.value))}
            onFocus={() => setCustomFocused(true)}
            onBlur={() => {
              setCustomFocused(false)
              setDraft(null)
            }}
            aria-label={ariaLabel}
            aria-invalid={alert || undefined}
            className={cn('px-2 text-right text-[13px]', alert && 'text-destructive')}
          />
        </InputGroup>
      </NumericKeypadPopover>
    )
  }

  return (
    <div className="flex w-full items-center gap-1.5">
      <Select value={selectValue} onValueChange={handleSelect} disabled={disabled}>
        <SelectTrigger aria-label={ariaLabel} className="h-9 min-w-0 flex-1 text-sm">
          <SelectValue placeholder="Set temp" />
        </SelectTrigger>
        <SelectContent
          onCloseAutoFocus={(event) => {
            if (focusCustomOnClose.current) {
              focusCustomOnClose.current = false
              event.preventDefault()
              customRef.current?.focus()
            }
          }}>
          {options.map((p) => (
            <SelectItem key={p.key} value={p.key}>
              {p.temperatureF === null ? p.label : `${p.label} · ${p.temperatureF.toFixed(1)}°F`}
            </SelectItem>
          ))}
          <SelectItem value="custom">Custom…</SelectItem>
        </SelectContent>
      </Select>
      {/* Keyboard-only numeric entry (no spinner controls) with a °F
          suffix, one decimal of precision. Only shown in Custom mode; the
          parent column is fixed-width so the row doesn't reflow when it
          appears. On a touch device the app's decimal keypad floats above it. */}
      {isCustom ? (
        <NumericKeypadPopover
          open={keypad && customFocused}
          decimal
          onKey={(key) => {
            if (key.kind === 'confirm') {
              customRef.current?.blur()
              return
            }
            commitText(applyDecimalKeypadKey(customText, key))
          }}>
          <InputGroup className="h-9 w-20 shrink-0">
            <InputGroupInput
              ref={customRef}
              inputMode={keypad ? 'none' : 'decimal'}
              value={customText}
              disabled={disabled}
              onChange={(e) => commitText(sanitizeDecimalText(e.target.value))}
              onFocus={() => setCustomFocused(true)}
              onBlur={() => {
                setCustomFocused(false)
                setDraft(null)
              }}
              aria-label={`${ariaLabel} custom temperature`}
              className="text-sm"
            />
            <InputGroupAddon align="inline-end">
              <InputGroupText className="text-xs">°F</InputGroupText>
            </InputGroupAddon>
          </InputGroup>
        </NumericKeypadPopover>
      ) : null}
    </div>
  )
}
